import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Label } from "@jambushrushti/components/ui/label";
import { useToast } from "@jambushrushti/hooks/use-toast";

import {
  Scroll,
  Mail,
  Lock,
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
} from "lucide-react";
import { useAdminAuth } from "@jambushrushti/contexts/AdminAuthContext";

const LMSLogin = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { login, isLoading: authLoading } = useAdminAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  // Only redirect after login completes with a valid redirect path
  useEffect(() => {
    if (redirectTo) {
      navigate(redirectTo, { replace: true });
    }
  }, [redirectTo, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const result = await login(email, password);

    if (result.success && result.redirectTo) {
      toast({
        title: "Welcome to LMS",
        description: "You have been logged in successfully.",
      });
      setRedirectTo(result.redirectTo);
    } else {
      toast({
        title: "Login Failed",
        description: result.error || "Please check your email and password.",
        variant: "destructive",
      });
    }

    setIsLoading(false);
  };

  if (authLoading) {
    return (
      <div
        className="lotus-pattern flex min-h-screen items-center justify-center"
        style={{ backgroundColor: "#FAF7F2" }}
      >
        <Loader2
          className="h-8 w-8 animate-spin"
          style={{ color: "#D2811D" }}
        />
      </div>
    );
  }

  return (
    <div
      className="lotus-pattern flex min-h-screen items-center justify-center p-4"
      style={{ backgroundColor: "#FAF7F2" }}
    >
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="animate-fade-up mb-8 text-center">
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="shadow-glow flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-primary to-gold">
              <Scroll className="h-6 w-6 text-primary-foreground" />
            </div>
            <span className="font-display text-2xl font-bold text-foreground">
              Jambushrusti LMS
            </span>
          </Link>
        </div>

        <Card variant="elevated" className="animate-fade-up delay-100">
          <CardHeader className="text-center">
            <CardTitle>Library Management System</CardTitle>
            <CardDescription>
              Sign in with your Librarian or Admin account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    required
                    className="pl-10"
                    placeholder="your@email.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    required
                    className="pl-10 pr-10"
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                variant="hero"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In to LMS
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="animate-fade-up mt-6 text-center text-sm text-muted-foreground delay-200">
          <Link to="/" className="text-primary hover:underline">
            ← Back to main website
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LMSLogin;
